import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { ArrowRightLeft, X, MoveRight, BedDouble } from "lucide-react";
import { WARDS_NO_ALL } from "./parts";

export function TransferDialog({ admission, onClose, onDone }) {
  const [ward, setWard] = useState(admission.ward || "General");
  const [bedNo, setBedNo] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [availableBeds, setAvailableBeds] = useState([]);
  const [loadingBeds, setLoadingBeds] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoadingBeds(true);
      try {
        const { data } = await api.get(`/admissions/beds/available?ward=${encodeURIComponent(ward)}`);
        if (alive) setAvailableBeds(data || []);
      } catch { if (alive) setAvailableBeds([]); }
      finally { if (alive) setLoadingBeds(false); }
    })();
    return () => { alive = false; };
  }, [ward]);

  const submit = async (e) => {
    e.preventDefault();
    if (ward === admission.ward && !bedNo.trim()) {
      toast.error("Choose a different ward or a new bed to transfer");
      return;
    }
    try {
      setSaving(true);
      await api.post(`/admissions/${admission.admission_id}/transfer`, {
        ward,
        bed_no: bedNo.trim() || null,
        reason: reason.trim() || null,
      });
      toast.success("Patient transferred");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to transfer");
    } finally { setSaving(false); }
  };

  const transfers = admission.transfers || [];

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl w-full max-w-lg shadow-xl border border-slate-200" data-testid="transfer-dialog">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="font-semibold flex items-center gap-2"><ArrowRightLeft className="w-4 h-4 text-[#1976D2]" /> Transfer patient</div>
            <div className="text-xs text-slate-500 mt-0.5">Currently: {admission.ward}{admission.bed_no ? ` · Bed ${admission.bed_no}` : ""}</div>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500 font-medium">New ward *</label>
              <select data-testid="transfer-ward" value={ward} onChange={(e) => { setWard(e.target.value); setBedNo(""); }} className="w-full h-10 mt-1 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                {WARDS_NO_ALL.map((w) => <option key={w} value={w}>{w}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 font-medium">Bed # {loadingBeds ? <span className="text-slate-400">(loading…)</span> : <span className="text-slate-400">({availableBeds.length} free)</span>}</label>
              {availableBeds.length > 0 ? (
                <select data-testid="transfer-bed" value={bedNo} onChange={(e) => setBedNo(e.target.value)} className="w-full h-10 mt-1 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                  <option value="">Select a free bed</option>
                  {availableBeds.map((b) => <option key={b.bed_id} value={b.bed_no}>{b.bed_no}</option>)}
                </select>
              ) : (
                <input data-testid="transfer-bed" value={bedNo} onChange={(e) => setBedNo(e.target.value)} placeholder="e.g. 4" className="w-full h-10 mt-1 px-3 rounded-lg border border-slate-200 text-sm" />
              )}
            </div>
          </div>
          {availableBeds.length > 0 && (
            <div className="rounded-lg bg-blue-50 border border-blue-100 p-2 flex items-start gap-2" data-testid="transfer-open-beds">
              <BedDouble className="w-4 h-4 text-[#1565C0] mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <div className="text-[11px] text-[#1565C0] font-medium uppercase mb-1">Open beds in {ward}</div>
                <div className="flex flex-wrap gap-1">
                  {availableBeds.slice(0, 12).map((b) => (
                    <button
                      type="button"
                      key={b.bed_id}
                      onClick={() => setBedNo(b.bed_no)}
                      data-testid={`open-bed-${b.bed_no}`}
                      className={`h-6 px-2 rounded text-[11px] font-medium border transition ${bedNo === b.bed_no ? "bg-[#1976D2] text-white border-[#1976D2]" : "bg-white text-[#1565C0] border-blue-200 hover:bg-blue-100"}`}
                    >{b.bed_no}</button>
                  ))}
                  {availableBeds.length > 12 && <span className="text-[11px] text-slate-500 self-center">+{availableBeds.length - 12} more</span>}
                </div>
              </div>
            </div>
          )}
          <div>
            <label className="text-xs text-slate-500 font-medium">Reason</label>
            <textarea data-testid="transfer-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="e.g. Requires ICU-level monitoring" className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm" />
          </div>
          {transfers.length > 0 && (
            <div className="pt-2 border-t border-slate-100">
              <div className="text-[11px] uppercase text-slate-500 mb-2">Transfer history</div>
              <ul className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="transfer-history">
                {transfers.slice().reverse().map((t) => (
                  <li key={`${t.at}-${t.to_ward}`} className="text-xs text-slate-600 flex items-center gap-1.5">
                    <span className="tabular text-slate-500">{new Date(t.at).toLocaleString()}</span>
                    <span className="font-medium">{t.from_ward}</span>
                    <MoveRight className="w-3 h-3 text-slate-400" />
                    <span className="font-medium">{t.to_ward}{t.bed_no ? ` · Bed ${t.bed_no}` : ""}</span>
                    <span className="text-slate-400">· by {t.by}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50 rounded-b-2xl">
          <button type="button" onClick={onClose} className="h-9 px-3 rounded-lg text-sm text-slate-600 hover:bg-slate-100">Cancel</button>
          <button data-testid="transfer-submit" disabled={saving} type="submit" className="h-9 px-4 rounded-lg bg-[#1976D2] text-white text-sm font-medium disabled:opacity-60">{saving ? "Transferring…" : "Confirm transfer"}</button>
        </div>
      </form>
    </div>
  );
}
